import { useState, useRef, useEffect } from "react";
import { Send, Sparkles, Loader2, Crown, Bot, User } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function ProBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-primary/10 text-primary rounded-full px-2 py-1"
      title="A Pro feature — free while we finish setting up checkout"
      data-testid="pro-badge"
    >
      <Crown className="h-3 w-3" /> Pro · free
    </span>
  );
}

const STARTERS = [
  "Explain this lesson's main idea simply",
  "Give me a real-world example",
  "What should I remember from this?",
];

export function AskAI({ videoId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/pro/ask", { video_id: videoId, question: q, history, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't answer that right now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border shadow-sm rounded-2xl p-6 md:p-7" data-testid="ask-ai">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary"><Sparkles className="h-4 w-4" /></span>
          <h3 className="font-heading font-bold text-xl">Ask AI</h3>
        </div>
        <ProBadge />
      </div>
      <p className="mt-2 text-sm text-muted-foreground">Stuck on something in this lesson? Ask your AI tutor anything.</p>

      <div className="mt-4 space-y-3 max-h-[380px] overflow-y-auto pr-1">
        {messages.length === 0 && (
          <div className="flex flex-wrap gap-2">
            {STARTERS.map((s) => (
              <button key={s} data-testid="ask-starter" onClick={() => send(s)}
                className="text-xs font-semibold border border-border rounded-full px-3 py-1.5 hover:bg-accent brutal-press">
                {s}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <span className={`grid place-items-center h-7 w-7 shrink-0 rounded-lg ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"}`}>
              {m.role === "user" ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
            </span>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2">
            <span className="grid place-items-center h-7 w-7 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"><Bot className="h-3.5 w-3.5" /></span>
            <div className="bg-secondary rounded-2xl px-3.5 py-2.5"><Loader2 className="h-4 w-4 animate-spin" /></div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2">
        <input
          data-testid="ask-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your question…"
          className="flex-1 rounded-full border border-border bg-background px-4 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        />
        <Button type="submit" data-testid="ask-send" disabled={loading || !input.trim()} className="bg-primary text-primary-foreground shrink-0">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
